import { useState, useEffect } from 'react';
import {
  updatePass,
  deletePass,
  updateAdminUser,
  updateBookingOnline,
  patchAdminBooking,
  createPasswordResetLink,
  revokeCourseAccess,
  resetUserNameToAuth,
} from '../../../services/adminService';
import { useAppContext } from '../../../context/AppContext';
import { DaysOfTheWeekTrans } from '../../../utils/translations';
import { formatPassPriceShort } from '../../../utils/currencyFormat';
import AddPassModal from './AddPassModal';
import MergeUsersModal from './MergeUsersModal';
import EditPassModal from '../EditPassModal';
import BookingsByPass from '../../shared/BookingsByPass';

const translatePassName = (passTypeOrName) => {
  if (passTypeOrName && typeof passTypeOrName === 'object') {
    return passTypeOrName.displayName || passTypeOrName.name || 'Unknown pass';
  }
  return passTypeOrName || 'Unknown pass';
};

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

export default function UserDetailsModal({ 
  userDetails, 
  detailsLoading, 
  onClose, 
  onRefresh,
  onUserUpdated,
  onMergeComplete,
}) {
  const { setToaster, token, language } = useAppContext();
  const [showAddPass, setShowAddPass] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [savingRegularDays, setSavingRegularDays] = useState(false);
  const [localRegularDays, setLocalRegularDays] = useState([]);
  const [regularDaysDirty, setRegularDaysDirty] = useState(false);
  const [updatingOnlineId, setUpdatingOnlineId] = useState(null);
  const [updatingAttendedId, setUpdatingAttendedId] = useState(null);
  const [updatingPaidId, setUpdatingPaidId] = useState(null);
  const [deletingPassId, setDeletingPassId] = useState(null);
  const [selectedPass, setSelectedPass] = useState(null);
  const [editingUser, setEditingUser] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [creatingResetLink, setCreatingResetLink] = useState(false);
  const [revokingPurchaseId, setRevokingPurchaseId] = useState(null);
  const [resettingName, setResettingName] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    given_name: '',
    family_name: '',
    email: '',
    role: 'basic',
    phoneNumber: '',
    userSource: '',
    notes: '',
  });

  const daysTrans = DaysOfTheWeekTrans[language] || DaysOfTheWeekTrans.english;

  // Sync local regular days from user when not dirty (e.g. after save or when userDetails loads)
  useEffect(() => {
    if (!regularDaysDirty && userDetails?.user) {
      setLocalRegularDays(Array.isArray(userDetails.user.regularDays) ? [...userDetails.user.regularDays] : []);
    }
  }, [userDetails?.user?.regularDays, regularDaysDirty, userDetails?.user]);

  // When viewing a different user, reset dirty so we sync from their data
  useEffect(() => {
    if (userDetails?.user?.user_id) {
      setRegularDaysDirty(false);
    }
  }, [userDetails?.user?.user_id]);

  const startEditingUser = () => {
    const u = userDetails?.user;
    if (!u) return;
    setEditForm({
      name: u.name ?? '',
      given_name: u.given_name ?? '',
      family_name: u.family_name ?? '',
      email: u.email ?? '',
      role: u.role ?? 'basic',
      phoneNumber: u.phoneNumber ?? '',
      userSource: u.userSource ?? '',
      notes: u.notes ?? '',
    });
    setEditingUser(true);
  };

  const handleSaveUser = async () => {
    if (!userDetails?.user?.user_id) return;
    setSavingUser(true);
    try {
      await updateAdminUser(token, userDetails.user.user_id, {
        name: editForm.name || undefined,
        given_name: editForm.given_name || undefined,
        family_name: editForm.family_name || undefined,
        email: editForm.email || undefined,
        role: editForm.role,
        phoneNumber: editForm.phoneNumber || undefined,
        userSource: editForm.userSource || '',
        notes: editForm.notes || '',
      });
      setEditingUser(false);
      onRefresh();
      onUserUpdated?.();
      setToaster({ message: 'User updated', type: 'success', show: true });
    } catch (err) {
      setToaster({ message: err.message || 'Failed to update user', type: 'error', show: true });
    } finally {
      setSavingUser(false);
    }
  };

  const handleResetName = async () => {
    if (!userDetails?.user?.user_id) return;
    setResettingName(true);
    try {
      await resetUserNameToAuth(token, userDetails.user.user_id);
      onRefresh();
      onUserUpdated?.();
      setToaster({ message: 'Name reverted to Google/Auth0 value', type: 'success', show: true });
    } catch (err) {
      setToaster({ message: err.message || 'Failed to revert name', type: 'error', show: true });
    } finally {
      setResettingName(false);
    }
  };

  const handleToggleOnline = async (booking) => {
    if (booking.status !== 'confirmed') return;
    const newOnline = !booking.online;
    setUpdatingOnlineId(booking._id);
    try {
      await updateBookingOnline(token, booking.classId, booking._id, { online: newOnline });
      onRefresh();
      setToaster({ message: `Booking set to ${newOnline ? 'online' : 'in-studio'}`, type: 'success', show: true });
    } catch (err) {
      console.error('Error updating booking:', err);
      setToaster({ message: err.message || 'Failed to update booking', type: 'error', show: true });
    } finally {
      setUpdatingOnlineId(null);
    }
  };

  const handleToggleAttended = async (booking) => {
    if (booking.status !== 'confirmed') return;
    const newAttended = booking.attended !== false ? false : true;
    setUpdatingAttendedId(booking._id);
    try {
      await patchAdminBooking(token, booking.classId, booking._id, { attended: newAttended });
      onRefresh();
      setToaster({
        message: newAttended ? 'Marked as attended' : 'Marked as absent (no-show)',
        type: 'success',
        show: true,
      });
    } catch (err) {
      console.error('Error updating attendance:', err);
      setToaster({ message: err.message || 'Failed to update attendance', type: 'error', show: true });
    } finally {
      setUpdatingAttendedId(null);
    }
  };

  const handleTogglePaid = async (pass) => {
    const newPaidStatus = !pass.paid;
    const confirmMessage = newPaidStatus 
      ? 'Mark this pass as paid?' 
      : 'Mark this pass as not paid?';
    
    if (!window.confirm(confirmMessage)) return;

    setUpdatingPaidId(pass._id);
    try {
      await updatePass(token, pass._id, { paid: newPaidStatus });
      setSelectedPass((prev) => (prev && prev._id === pass._id ? { ...prev, paid: newPaidStatus } : prev));
      onRefresh();
      setToaster({
        message: `Pass marked as ${newPaidStatus ? 'paid' : 'not paid'}`,
        type: "success",
        show: true,
      });
    } catch (error) {
      setToaster({
        message: "Failed to update pass",
        type: "error",
        show: true,
      });
    } finally {
      setUpdatingPaidId(null);
    }
  };

  const handleDeletePass = async (pass) => {
    const passName = pass.passTypeId?.name || 'this pass';
    if (!window.confirm(`Are you sure you want to delete ${passName}? This action cannot be undone.`)) {
      return;
    }

    setDeletingPassId(pass._id);
    try {
      await deletePass(token, pass._id);
      onRefresh();
      setToaster({
        message: "Pass deleted successfully",
        type: "success",
        show: true,
      });
    } catch (error) {
      setToaster({
        message: "Failed to delete pass",
        type: "error",
        show: true,
      });
    } finally {
      setDeletingPassId(null);
    }
  };

  const handleAddPassSuccess = () => {
    setShowAddPass(false);
    onRefresh();
  };

  const handleRevokeAccess = async (purchase) => {
    if (!userDetails?.user?.user_id) return;
    if (!window.confirm(`Revoke access to "${purchase.courseId?.title || 'this course'}"?`)) return;
    setRevokingPurchaseId(purchase._id);
    try {
      await revokeCourseAccess(token, userDetails.user.user_id, purchase._id);
      onRefresh();
      setToaster({ message: 'Course access revoked', type: 'success', show: true });
    } catch (err) {
      setToaster({ message: err.message || 'Failed to revoke course access', type: 'error', show: true });
    } finally {
      setRevokingPurchaseId(null);
    }
  };

  const currentUserId = userDetails?.user?.user_id || '';
  const isPlaceholderUser = currentUserId.startsWith('local|');
  const isDatabasePasswordUser = currentUserId.startsWith('auth0|');
  const canCreatePasswordResetLink = isDatabasePasswordUser && !isPlaceholderUser;

  const handleCopyPasswordResetLink = async () => {
    if (!userDetails?.user?.user_id) return;
    if (!canCreatePasswordResetLink) {
      setToaster({
        message: 'Password reset links are only available for database-password users.',
        type: 'error',
        show: true,
      });
      return;
    }

    setCreatingResetLink(true);
    try {
      const data = await createPasswordResetLink(token, userDetails.user.user_id);
      await navigator.clipboard.writeText(data.resetLink);
      setToaster({
        message: 'Password reset link copied to clipboard.',
        type: 'success',
        show: true,
      });
    } catch (err) {
      setToaster({
        message: err.message || 'Failed to create password reset link',
        type: 'error',
        show: true,
      });
    } finally {
      setCreatingResetLink(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4">
        <div className="bg-white rounded-t-2xl sm:rounded-lg p-4 sm:p-8 max-w-2xl w-full max-h-[92vh] sm:max-h-[90vh] overflow-y-auto shadow-xl">
          <div className="flex justify-between items-center mb-4 sm:mb-6 gap-2">
            <h2 className="text-xl sm:text-2xl font-bold truncate">User Details</h2>
            <button
              type="button"
              onClick={onClose}
              className="flex-shrink-0 w-10 h-10 sm:w-auto sm:h-auto flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full sm:rounded text-2xl font-bold transition-colors"
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          {detailsLoading ? (
            <p className="text-center py-4">Loading...</p>
          ) : userDetails ? (
            <>
              {/* User Info */}
              <div className="mb-4 sm:mb-6 pb-4 sm:pb-6 border-b border-gray-200">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3 sm:mb-4">
                  <h3 className="text-base sm:text-lg font-semibold">User Information</h3>
                  {!editingUser ? (
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setShowMergeModal(true)}
                        className="px-3 py-1.5 rounded-lg border border-amber-300 text-sm font-medium text-amber-900 bg-amber-50 hover:bg-amber-100"
                      >
                        Merge duplicate…
                      </button>
                      <button
                        type="button"
                        onClick={handleCopyPasswordResetLink}
                        disabled={creatingResetLink || !canCreatePasswordResetLink}
                        className="px-3 py-1.5 rounded-lg border border-blue-300 text-sm font-medium text-blue-900 bg-blue-50 hover:bg-blue-100 disabled:opacity-50"
                        title={
                          !canCreatePasswordResetLink
                            ? 'Only Auth0 database users (auth0|...) can receive password reset links'
                            : ''
                        }
                      >
                        {creatingResetLink ? 'Creating link…' : 'Copy reset link'}
                      </button>
                      <button
                        type="button"
                        onClick={startEditingUser}
                        className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Edit user
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setEditingUser(false)}
                        className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveUser}
                        disabled={savingUser}
                        className="px-3 py-1.5 rounded-lg bg-brown text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
                      >
                        {savingUser ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div className="min-w-0 sm:col-span-2">
                    <p className="text-sm text-gray-600">User ID</p>
                    <p className="font-semibold text-xs break-all">{userDetails.user.user_id}</p>
                  </div>
                  {editingUser ? (
                    <>
                      <div className="min-w-0">
                        <label className="block text-sm text-gray-600 mb-0.5">Name (full)</label>
                        <input
                          type="text"
                          value={editForm.name}
                          onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                          placeholder="Full name"
                        />
                      </div>
                      <div className="min-w-0">
                        <label className="block text-sm text-gray-600 mb-0.5">Given name</label>
                        <input
                          type="text"
                          value={editForm.given_name}
                          onChange={(e) => setEditForm((f) => ({ ...f, given_name: e.target.value }))}
                          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                        />
                      </div>
                      <div className="min-w-0">
                        <label className="block text-sm text-gray-600 mb-0.5">Family name</label>
                        <input
                          type="text"
                          value={editForm.family_name}
                          onChange={(e) => setEditForm((f) => ({ ...f, family_name: e.target.value }))}
                          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                        />
                      </div>
                      <div className="min-w-0">
                        <label className="block text-sm text-gray-600 mb-0.5">Email</label>
                        <input
                          type="email"
                          value={editForm.email}
                          onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                        />
                      </div>
                      <div className="min-w-0">
                        <label className="block text-sm text-gray-600 mb-0.5">Role</label>
                        <select
                          value={editForm.role}
                          onChange={(e) => setEditForm((f) => ({ ...f, role: e.target.value }))}
                          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                        >
                          <option value="basic">basic</option>
                          <option value="studio">studio</option>
                          <option value="onlineClub">onlineClub</option>
                          <option value="admin">admin</option>
                        </select>
                      </div>
                      <div className="min-w-0">
                        <label className="block text-sm text-gray-600 mb-0.5">Phone</label>
                        <input
                          type="tel"
                          value={editForm.phoneNumber}
                          onChange={(e) => setEditForm((f) => ({ ...f, phoneNumber: e.target.value }))}
                          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                          placeholder="Phone number"
                        />
                      </div>
                      <div className="min-w-0">
                        <label className="block text-sm text-gray-600 mb-0.5">Source</label>
                        <select
                          value={editForm.userSource}
                          onChange={(e) => setEditForm((f) => ({ ...f, userSource: e.target.value }))}
                          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                        >
                          <option value="">—</option>
                          <option value="other">other</option>
                          <option value="socials">socials</option>
                          <option value="target">target</option>
                          <option value="ads">ads</option>
                          <option value="friends">friends</option>
                          <option value="organic">organic</option>
                        </select>
                      </div>
                      <div className="min-w-0 sm:col-span-2">
                        <label className="block text-sm text-gray-600 mb-0.5">Admin notes (internal)</label>
                        <textarea
                          value={editForm.notes}
                          onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
                          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm min-h-[90px]"
                          placeholder="Visible only in admin dashboard"
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="min-w-0">
                        <p className="text-sm text-gray-600">Email</p>
                        <p className="font-semibold text-sm break-all">{userDetails.user.email || '—'}</p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-gray-600">Role</p>
                        <p className="font-semibold">{userDetails.user.role}</p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-gray-600 flex items-center gap-1.5">
                          Name
                          {userDetails.user.nameEditedByAdmin && (
                            <span
                              className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800"
                              title="Set manually by an admin; no longer synced from Google/Auth0"
                            >
                              Manually set
                            </span>
                          )}
                        </p>
                        <p className="font-semibold">{userDetails.user.name || [userDetails.user.given_name, userDetails.user.family_name].filter(Boolean).join(' ') || '—'}</p>
                        {userDetails.user.nameEditedByAdmin && (
                          <button
                            type="button"
                            onClick={handleResetName}
                            disabled={resettingName}
                            className="mt-1 text-xs text-blue-600 hover:underline disabled:opacity-50"
                          >
                            {resettingName ? 'Reverting…' : 'Revert to Google/Auth0 name'}
                          </button>
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-gray-600">Phone</p>
                        <p className="font-semibold text-sm break-all">{userDetails.user.phoneNumber || '—'}</p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-gray-600">Source</p>
                        <p className="font-semibold text-sm break-all">{userDetails.user.userSource || '—'}</p>
                      </div>
                      <div className="min-w-0 sm:col-span-2">
                        <p className="text-sm text-gray-600">Admin notes</p>
                        <p className="font-semibold text-sm whitespace-pre-wrap break-words">{userDetails.user.notes || '—'}</p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Regular days (auto-booking) — same flow as Profile: local state + Save when dirty */}
              <div className="mb-4 sm:mb-6 pb-4 sm:pb-6 border-b border-gray-200">
                <h3 className="text-base sm:text-lg font-semibold mb-1">Regular days</h3>
                <p className="text-sm text-gray-600 mb-4">Classes on these days will be auto-booked when the student has an active pass.</p>
                <div className="grid grid-cols-4 sm:grid-cols-7 gap-2 sm:gap-3 mb-4">
                  {DAY_KEYS.map((key, index) => {
                    const dayNum = index;
                    const checked = localRegularDays.includes(dayNum);
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => {
                          const next = checked
                            ? localRegularDays.filter((d) => d !== dayNum)
                            : [...localRegularDays, dayNum].sort((a, b) => a - b);
                          setLocalRegularDays(next);
                          setRegularDaysDirty(true);
                        }}
                        disabled={savingRegularDays}
                        className={`
                          py-2 px-1 rounded-xl text-sm font-semibold transition-all duration-200 touch-manipulation
                          focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2
                          ${checked
                            ? "bg-amber-600 text-white shadow-md shadow-amber-200/50 hover:bg-amber-700 active:scale-[0.98]"
                            : "bg-amber-50/80 text-amber-800 border-2 border-amber-200 hover:border-amber-300 hover:bg-amber-100/80 active:scale-[0.98]"
                          }
                        `}
                      >
                        {daysTrans[key]}
                      </button>
                    );
                  })}
                </div>
                {regularDaysDirty && (
                  <button
                    type="button"
                    onClick={async () => {
                      setSavingRegularDays(true);
                      try {
                        await updateAdminUser(token, userDetails.user.user_id, { regularDays: localRegularDays });
                        onRefresh();
                        setRegularDaysDirty(false);
                        setToaster({ message: 'Regular days updated', type: 'success', show: true });
                      } catch (err) {
                        setToaster({ message: 'Failed to update', type: 'error', show: true });
                      } finally {
                        setSavingRegularDays(false);
                      }
                    }}
                    disabled={savingRegularDays}
                    className="w-full sm:w-auto min-w-[120px] py-2 px-3 rounded-xl bg-amber-600 text-white shadow-md hover:bg-amber-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                  >
                    {savingRegularDays ? 'Saving…' : 'Save'}
                  </button>
                )}
              </div>

              {/* Passes */}
              <div className="mb-4 sm:mb-6 pb-4 sm:pb-6 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-2 mb-3 sm:mb-4">
                  <h3 className="text-base sm:text-lg font-semibold">Passes</h3>
                  <button
                    type="button"
                    onClick={() => setShowAddPass(true)}
                    className="bg-brown text-white px-4 py-2 rounded-lg text-sm font-semibold hover:opacity-90 w-full sm:w-auto"
                  >
                    + Add Pass
                  </button>
                </div>
                {userDetails.user.passes && userDetails.user.passes.length > 0 ? (
                  <div className="space-y-3">
                    {[...userDetails.user.passes]
                      .sort((a, b) => new Date(b.purchasedAt || 0) - new Date(a.purchasedAt || 0))
                      .map((pass) => (
                      <div
                        key={pass._id}
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedPass(pass)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') setSelectedPass(pass);
                        }}
                        className="border border-gray-200 rounded-lg p-3 sm:p-4 cursor-pointer hover:bg-gray-50"
                      >
                        <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-start mb-2">
                          <div className="min-w-0">
                            <p className="font-semibold text-sm sm:text-base">{pass.passTypeId?.name || 'Unknown Pass'}</p>
                            <p className="text-xs text-gray-600">
                              {pass.classesIncluded ? `${pass.classesIncluded} classes` : 'Unlimited'}
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-2 items-center mx-auto md:mx-1">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTogglePaid(pass);
                              }}
                              className={`px-2.5 py-1.5 rounded text-xs font-semibold cursor-pointer hover:opacity-80 transition-opacity min-h-[36px] ${
                                pass.paid 
                                  ? 'bg-yellow-100 text-green-700 hover:bg-green-200' 
                                  : 'bg-orange-100 text-yellow-700 hover:bg-yellow-200'
                              }`}
                              title={pass.paid ? 'Click to mark as not paid' : 'Click to mark as paid'}
                            >
                              {pass.paid ? '💵 Paid' : '📝 Not Paid'}
                            </button>
                            <span className={`px-2.5 py-1 rounded text-xs font-semibold ${
                              pass.status === 'active' ? 'bg-green-100 text-green-700' :
                              pass.status === 'exhausted' ? 'bg-amber-100 text-amber-700' :
                              pass.status === 'expired' ? 'bg-red-100 text-red-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {pass.status}
                            </span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeletePass(pass);
                              }}
                              className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded min-h-[36px] min-w-[36px] flex items-center justify-center"
                              title="Delete pass"
                              aria-label="Delete pass"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                        <div className="text-xs text-gray-600 space-y-1">
                          <p>Classes Used: {pass.classesUsed} / {pass.classesIncluded || '∞'}</p>
                          <p>Expires: {new Date(pass.expiresAt).toLocaleDateString()}</p>
                          <p>Price: {pass.finalPrice} {pass.currency}</p>
                          {pass.discountName && (
                            <p className="text-green-600">Discount: {pass.discountName}</p>
                          )}
                          {pass.isSubscription && (
                            <p>Subscription: {pass.subscriptionActive ? 'Active' : 'Inactive'}</p>
                          )}
                          {pass.notes && (
                            <p className="text-gray-500 italic break-words">{pass.notes}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No passes</p>
                )}
              </div>

              {/* Courses */}
              <div className="mb-4 sm:mb-6 pb-4 sm:pb-6 border-b border-gray-200">
                <h3 className="text-base sm:text-lg font-semibold mb-1">Course access</h3>
                {['studio', 'onlineClub', 'admin'].includes(userDetails.user.role) && (
                  <p className="text-sm text-gray-600 mb-3">
                    This user's role ({userDetails.user.role}) grants access to all courses.
                  </p>
                )}
                {userDetails.user.purchasedCourses && userDetails.user.purchasedCourses.length > 0 ? (
                  <div className="space-y-2 mb-3">
                    {[...userDetails.user.purchasedCourses]
                      .sort((a, b) => new Date(b.purchasedAt || 0) - new Date(a.purchasedAt || 0))
                      .map((purchase) => (
                        <div key={purchase._id} className="border border-gray-200 rounded-lg p-3 flex flex-wrap items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="font-semibold text-sm">{purchase.courseId?.title || 'Unknown course'}</p>
                            <p className="text-xs text-gray-600">
                              {purchase.finalPrice > 0 && `${formatPassPriceShort(purchase.finalPrice, purchase.currency)} · `}
                              {new Date(purchase.purchasedAt).toLocaleDateString()}
                              {purchase.paymentProvider === 'manual' && ' · manually granted'}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className={`px-2.5 py-1 rounded text-xs font-semibold ${
                              purchase.status === 'paid' ? 'bg-green-100 text-green-700' :
                              purchase.status === 'refunded' ? 'bg-red-100 text-red-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {purchase.status}
                            </span>
                            {purchase.status === 'paid' && (
                              <button
                                type="button"
                                onClick={() => handleRevokeAccess(purchase)}
                                disabled={revokingPurchaseId === purchase._id}
                                className="text-red-600 text-xs hover:underline disabled:opacity-50"
                              >
                                {revokingPurchaseId === purchase._id ? '…' : 'Revoke'}
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No purchased courses</p>
                )}
              </div>

              {/* Bookings */}
              <div className="mb-4 sm:mb-6 pb-4 sm:pb-6 border-b border-gray-200">
                <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Bookings</h3>
                <BookingsByPass
                  bookings={(userDetails.bookings || []).map((booking) => ({
                    ...booking,
                    key: booking._id,
                    sortDate: new Date(booking.classStart).getTime(),
                  }))}
                  passes={userDetails.user.passes || []}
                  translatePassName={translatePassName}
                  emptyMessage="No bookings"
                  otherLabel="Other bookings"
                  classesCountLabel="bookings"
                  renderRow={(booking) => (
                    <div className="p-3 sm:p-4">
                      <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-start">
                        <div className="min-w-0">
                          <p className="font-semibold text-sm sm:text-base">{booking.classTitle}</p>
                          <p className="text-xs text-gray-600">
                            {new Date(booking.classStart).toLocaleString('en-GB', {
                              timeZone: 'Asia/Jerusalem',
                              weekday: 'short',
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5 self-start flex-shrink-0">
                          {booking.status === 'confirmed' ? (
                            <button
                              type="button"
                              onClick={() => handleToggleOnline(booking)}
                              disabled={updatingOnlineId === booking._id}
                              title={booking.online ? 'Click to set in-studio' : 'Click to set online'}
                              className={`px-2.5 py-1 rounded text-xs font-semibold cursor-pointer hover:opacity-90 disabled:opacity-50 ${
                                booking.online ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-700'
                              }`}
                            >
                              {updatingOnlineId === booking._id ? '…' : (booking.online ? 'Online' : 'Studio')}
                            </button>
                          ) : booking.online ? (
                            <span className="px-2.5 py-1 rounded text-xs font-semibold bg-blue-100 text-blue-800">Online</span>
                          ) : (
                            <span className="px-2.5 py-1 rounded text-xs font-semibold bg-slate-100 text-slate-700">Studio</span>
                          )}
                          {booking.status === 'confirmed' ? (
                            <button
                              type="button"
                              onClick={() => handleToggleAttended(booking)}
                              disabled={updatingAttendedId === booking._id}
                              title={booking.attended === false ? 'Click to mark attended' : 'Click to mark absent (no-show)'}
                              className={`px-2.5 py-1 rounded text-xs font-semibold cursor-pointer hover:opacity-90 disabled:opacity-50 ${
                                booking.attended === false ? 'bg-rose-100 text-rose-800' : 'bg-emerald-100 text-emerald-800'
                              }`}
                            >
                              {updatingAttendedId === booking._id ? '…' : (booking.attended === false ? 'Absent' : 'Attended')}
                            </button>
                          ) : booking.attended === false ? (
                            <span className="px-2.5 py-1 rounded text-xs font-semibold bg-rose-100 text-rose-800">Absent</span>
                          ) : null}
                          {booking.autoBooked === true && (
                            <span className="px-2.5 py-1 rounded text-xs font-semibold bg-amber-100 text-amber-800" title="Auto-booked (regular day)">
                              Auto
                            </span>
                          )}
                          <span className={`px-2.5 py-1 rounded text-xs font-semibold ${
                            booking.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                            booking.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {booking.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                />
              </div>

              {/* Subscription Renewals */}
              {userDetails.subscriptionRenewals && userDetails.subscriptionRenewals.length > 0 && (
                <div>
                  <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Subscription Renewals</h3>
                  <div className="space-y-2 sm:space-y-3">
                    {userDetails.subscriptionRenewals.map((renewal) => (
                      <div key={renewal._id} className="border border-gray-200 rounded-lg p-3 sm:p-4">
                        <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-start">
                          <div className="min-w-0">
                            <p className="font-semibold text-sm sm:text-base">Renewal #{renewal.renewalNumber}</p>
                            <p className="text-xs text-gray-600">
                              {new Date(renewal.periodStartDate).toLocaleDateString()} - {new Date(renewal.periodEndDate).toLocaleDateString()}
                            </p>
                            <p className="text-xs text-gray-600 mt-1">
                              {formatPassPriceShort(renewal.renewalPrice, renewal.passId?.currency)}
                            </p>
                          </div>
                          <span className={`px-2.5 py-1 rounded text-xs font-semibold self-start flex-shrink-0 ${
                            renewal.status === 'successful' ? 'bg-green-100 text-green-700' :
                            renewal.status === 'failed' ? 'bg-red-100 text-red-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {renewal.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="text-red-600">Failed to load user details</p>
          )}
        </div>
      </div>

      {/* Add Pass Modal */}
      {showAddPass && userDetails && (
        <AddPassModal
          userId={userDetails.user.user_id}
          onClose={() => setShowAddPass(false)}
          onSuccess={handleAddPassSuccess}
        />
      )}

      {showMergeModal && userDetails?.user && (
        <MergeUsersModal
          token={token}
          secondaryUserId={userDetails.user.user_id}
          secondarySummary={[userDetails.user.email, userDetails.user.name].filter(Boolean).join(' · ')}
          onClose={() => setShowMergeModal(false)}
          onSuccess={(keptUserId) => {
            setShowMergeModal(false);
            setToaster({ message: 'Accounts merged. Kept user loaded.', type: 'success', show: true });
            onMergeComplete?.(keptUserId);
          }}
        />
      )}

      {selectedPass && (
        <EditPassModal
          pass={selectedPass}
          token={token}
          onClose={() => setSelectedPass(null)}
          onSuccess={onRefresh}
          onDelete={async (passId) => {
            const pass = userDetails?.user?.passes?.find((p) => p._id === passId);
            if (!pass) return;
            await handleDeletePass(pass);
            setSelectedPass(null);
          }}
          onTogglePaid={(pass) => handleTogglePaid(pass)}
          deletingId={deletingPassId}
          updatingPaidId={updatingPaidId}
        />
      )}
    </>
  );
}